import { useState, useCallback } from 'react';
import { adminAPI } from '@/services/api';
import { User } from '@/types';
import { getErrorMessage } from '@/utils/errorUtils';

// 타입 정의
export interface ConfirmDialogState {
  target: User | null;
  open: boolean;
}

export interface EmailResultData {
  emailSent: boolean;
  emailError?: string | null;
  userEmail: string;
}

export interface ConfirmDialogsState {
  deactivate: ConfirmDialogState;
  activate: ConfirmDialogState;
  delete: ConfirmDialogState;
}

export interface EmailResultState {
  data: EmailResultData | null;
  dialogOpen: boolean;
}

export interface UserActionHandlers {
  approve: (userId: number, customerId?: number, companyName?: string) => Promise<void>;
  deactivate: {
    click: (user: User) => void;
    confirm: () => Promise<void>;
  };
  activate: {
    click: (user: User) => void;
    confirm: () => Promise<void>;
  };
  delete: {
    click: (user: User) => void;
    confirm: () => Promise<void>;
  };
  update: (user: User, originalUser: User | null, updates?: { auth_type?: string; new_password?: string }) => Promise<void>;
  password: (userId: number, newPassword: string, confirmPassword: string) => Promise<void>;
  authType: (userId: number, authType: 'password' | 'oauth' | 'pending_oauth') => Promise<void>;
}

export interface UserManagementActionsReturn {
  // 확인 다이얼로그 상태
  confirmDialogs: ConfirmDialogsState;
  setConfirmDialogs: (partial: Partial<ConfirmDialogsState>) => void;

  // 이메일 결과 상태
  emailResult: EmailResultState;
  setEmailResult: (partial: Partial<EmailResultState>) => void;

  // 승인 중인 사용자 ID
  approvingUserId: number | null;

  // 핸들러
  handlers: UserActionHandlers;
}

interface UseUserManagementActionsProps {
  setError: (message: string) => void;
  setSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
  refreshAllTabs: () => void;
}

/**
 * 사용자 관리 액션 hook
 *
 * 구조:
 * - confirmDialogs: 확인 다이얼로그 상태 { deactivate, activate, delete }
 * - emailResult: 이메일 결과 상태 { data, dialogOpen }
 * - approvingUserId: 승인 중인 사용자 ID
 * - handlers: 액션 핸들러 { approve, deactivate, activate, delete, update, password, authType }
 */
export const useUserManagementActions = ({
  setError,
  setSuccess,
  setLoading,
  refreshAllTabs,
}: UseUserManagementActionsProps): UserManagementActionsReturn => {
  // 확인 다이얼로그 상태
  const [confirmDialogsState, setConfirmDialogsState] = useState<ConfirmDialogsState>({
    deactivate: { target: null, open: false },
    activate: { target: null, open: false },
    delete: { target: null, open: false },
  });

  // 이메일 결과 상태
  const [emailResultState, setEmailResultState] = useState<EmailResultState>({
    data: null,
    dialogOpen: false,
  });

  // 승인 중인 사용자 ID
  const [approvingUserId, setApprovingUserId] = useState<number | null>(null);

  // 부분 업데이트 헬퍼 함수들
  const setConfirmDialogs = useCallback((partial: Partial<ConfirmDialogsState>) => {
    setConfirmDialogsState(prev => ({ ...prev, ...partial }));
  }, []);

  const setEmailResult = useCallback((partial: Partial<EmailResultState>) => {
    setEmailResultState(prev => ({ ...prev, ...partial }));
  }, []);

  // 핸들러 함수들
  const handleApproveUser = useCallback(async (userId: number, customerId?: number, companyName?: string) => {
    setApprovingUserId(userId);
    setError('');
    try {
      const response = await adminAPI.approveUser(userId, customerId, companyName);
      setError('');

      if (response.data?.email_sent !== undefined) {
        setEmailResultState({
          data: {
            emailSent: response.data.email_sent,
            emailError: response.data.email_error || null,
            userEmail: response.data.user_email || '',
          },
          dialogOpen: true,
        });
      }

      refreshAllTabs();
    } catch (error: unknown) {
      setError(getErrorMessage(error, '사용자 승인에 실패했습니다.'));
    } finally {
      setApprovingUserId(null);
    }
  }, [setError, refreshAllTabs]);

  const handleDeactivateUserClick = useCallback((user: User) => {
    setConfirmDialogsState(prev => ({
      ...prev,
      deactivate: { target: user, open: true },
    }));
  }, []);

  const handleDeactivateUser = useCallback(async () => {
    const user = confirmDialogsState.deactivate.target;
    if (!user) return;

    try {
      await adminAPI.deactivateUser(user.id);
      setConfirmDialogsState(prev => ({
        ...prev,
        deactivate: { target: null, open: false },
      }));
      setError('');
      refreshAllTabs();
    } catch (error: unknown) {
      const message = getErrorMessage(error, '사용자 비활성화에 실패했습니다.');
      setError(message);
      setConfirmDialogsState(prev => ({
        ...prev,
        deactivate: { ...prev.deactivate, open: false },
      }));
    }
  }, [confirmDialogsState.deactivate.target, setError, refreshAllTabs]);

  const handleActivateUserClick = useCallback((user: User) => {
    setConfirmDialogsState(prev => ({
      ...prev,
      activate: { target: user, open: true },
    }));
  }, []);

  const handleActivateUser = useCallback(async () => {
    const user = confirmDialogsState.activate.target;
    if (!user) return;

    try {
      await adminAPI.updateUser(user.id, { status: 'active' });
      setConfirmDialogsState(prev => ({
        ...prev,
        activate: { target: null, open: false },
      }));
      setError('');
      setSuccess('사용자가 활성화되었습니다.');
      refreshAllTabs();
    } catch (error: unknown) {
      const message = getErrorMessage(error, '사용자 활성화에 실패했습니다.');
      setError(message);
      setConfirmDialogsState(prev => ({
        ...prev,
        activate: { ...prev.activate, open: false },
      }));
    }
  }, [confirmDialogsState.activate.target, setError, setSuccess, refreshAllTabs]);

  const handleDeleteUserClick = useCallback((user: User) => {
    setConfirmDialogsState(prev => ({
      ...prev,
      delete: { target: user, open: true },
    }));
  }, []);

  const handleDeleteUser = useCallback(async () => {
    const user = confirmDialogsState.delete.target;
    if (!user) return;

    try {
      await adminAPI.deleteUser(user.id);
      setConfirmDialogsState(prev => ({
        ...prev,
        delete: { target: null, open: false },
      }));
      setError('');
      setSuccess('사용자가 삭제되었습니다.');
      refreshAllTabs();
    } catch (error: unknown) {
      const message = getErrorMessage(error, '사용자 삭제에 실패했습니다.');
      setError(message);
      setConfirmDialogsState(prev => ({
        ...prev,
        delete: { ...prev.delete, open: false },
      }));
    }
  }, [confirmDialogsState.delete.target, setError, setSuccess, refreshAllTabs]);

  const handleUpdateUser = useCallback(async (
    user: User,
    originalUser: User | null,
    updates?: { auth_type?: string; new_password?: string }
  ) => {
    if (!originalUser) return;

    setLoading(true);
    setError('');

    try {
      const updatedData: Record<string, unknown> = {};

      if (user.name !== originalUser.name) {
        updatedData.name = user.name;
      }
      if (user.role !== originalUser.role) {
        updatedData.role = user.role;
      }
      if (user.status !== originalUser.status) {
        updatedData.status = user.status;
      }
      if (user.permission !== originalUser.permission && user.role === 'user') {
        updatedData.permission = user.permission;
      }

      if (updates?.auth_type) {
        const passwordForAuthType = (updates.auth_type === 'password' && updates.new_password)
          ? updates.new_password
          : undefined;
        await adminAPI.adminChangeAuthType(
          user.id,
          updates.auth_type as 'password' | 'oauth' | 'pending_oauth',
          passwordForAuthType
        );
      } else if (updates?.new_password) {
        await adminAPI.adminResetPassword(user.id, updates.new_password);
      }

      if (Object.keys(updatedData).length > 0) {
        await adminAPI.updateUser(user.id, updatedData);
      }

      refreshAllTabs();
    } catch (error: unknown) {
      setError(getErrorMessage(error, '사용자 정보 수정에 실패했습니다.'));
    } finally {
      setLoading(false);
    }
  }, [setError, setLoading, refreshAllTabs]);

  const handlePasswordSubmit = useCallback(async (
    userId: number,
    newPassword: string,
    _confirmPassword: string
  ) => {
    try {
      await adminAPI.adminResetPassword(userId, newPassword);
      refreshAllTabs();
    } catch (error: unknown) {
      setError(getErrorMessage(error, '비밀번호 변경에 실패했습니다.'));
    }
  }, [setError, refreshAllTabs]);

  const handleAuthTypeSubmit = useCallback(async (
    userId: number,
    authType: 'password' | 'oauth' | 'pending_oauth'
  ) => {
    try {
      await adminAPI.adminChangeAuthType(userId, authType);
      refreshAllTabs();
    } catch (error: unknown) {
      setError(getErrorMessage(error, '인증 유형 변경에 실패했습니다.'));
    }
  }, [setError, refreshAllTabs]);

  // 그룹화된 핸들러 객체
  const handlers: UserActionHandlers = {
    approve: handleApproveUser,
    deactivate: {
      click: handleDeactivateUserClick,
      confirm: handleDeactivateUser,
    },
    activate: {
      click: handleActivateUserClick,
      confirm: handleActivateUser,
    },
    delete: {
      click: handleDeleteUserClick,
      confirm: handleDeleteUser,
    },
    update: handleUpdateUser,
    password: handlePasswordSubmit,
    authType: handleAuthTypeSubmit,
  };

  return {
    confirmDialogs: confirmDialogsState,
    setConfirmDialogs,
    emailResult: emailResultState,
    setEmailResult,
    approvingUserId,
    handlers,
  };
};
